<template>
  <v-container fluid>
    <div class="d-flex align-start justify-space-between flex-wrap header-row">
      <div>
        <h2 class="page-title mb-1">Métricas de Reembolsos</h2>
        <p class="page-desc mb-0">
          Rechazo, corrección y comportamiento por servicio.
        </p>
      </div>

      <v-card outlined class="total-badge text-right">
        <div class="kpi-label total-badge-label">Total de reembolsos</div>
        <div class="total-badge-value">{{ insights.total != null ? insights.total : '—' }}</div>
      </v-card>
    </div>

    <div class="d-flex align-center flex-wrap mb-4 mt-4" style="gap: 8px;">
      <span class="toggle-label">Periodo:</span>
      <v-btn-toggle v-model="period" mandatory color="primary" dense class="period-toggle">
        <v-btn value="historical">Histórico</v-btn>
        <v-btn value="month">Mes</v-btn>
        <v-btn value="quarter">Q</v-btn>
      </v-btn-toggle>

      <div v-if="period !== 'historical'" class="d-flex align-center period-nav">
        <v-btn icon small @click="periodOffset -= 1">
          <v-icon small>mdi-chevron-left</v-icon>
        </v-btn>
        <span class="period-nav-label">{{ periodLabel }}</span>
        <v-btn icon small :disabled="!canGoNext" @click="periodOffset += 1">
          <v-icon small>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Rechazo -->
    <div class="row-label" style="color: #D32F2F;">Rechazo</div>
    <v-row class="mb-4">
      <v-col cols="12" sm="4">
        <v-card
          outlined
          class="kpi-card pa-4 text-center"
          :class="{ 'kpi-card--clickable': trend.length > 1 }"
          @click="trend.length > 1 && openTrendDialog('rejectionRate')"
        >
          <div class="kpi-label">Tasa de rechazo</div>
          <div v-if="loadingKpi" class="py-2">
            <v-progress-circular indeterminate size="20" width="2" color="primary" />
          </div>
          <template v-else>
            <div class="kpi-value">{{ rejectionRateLabel }}</div>
            <RefundSparkline :data="trendRejectionRate" color="#D32F2F" />
          </template>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card
          outlined
          class="kpi-card pa-4 text-center"
          :class="{ 'kpi-card--clickable': trend.length > 1 }"
          @click="trend.length > 1 && openTrendDialog('rejected')"
        >
          <div class="kpi-label">Reembolsos rechazados</div>
          <div v-if="loadingKpi" class="py-2">
            <v-progress-circular indeterminate size="20" width="2" color="primary" />
          </div>
          <template v-else>
            <div class="kpi-value">{{ insights.rejected != null ? insights.rejected : '—' }}</div>
            <RefundSparkline :data="trendRejected" color="#D32F2F" />
          </template>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined class="stat-card pa-3 text-center">
          <div class="stat-label">Servicio más rechazado</div>
          <div v-if="loadingInsights" class="py-2">
            <v-progress-circular indeterminate size="20" width="2" color="primary" />
          </div>
          <template v-else>
            <div class="stat-value">{{ topRejectedServiceLabel }}</div>
            <div v-if="insights.topRejectedService" class="stat-sub">
              {{ insights.topRejectedService.count }} rechazos
            </div>
          </template>
        </v-card>
      </v-col>
    </v-row>

    <!-- Corrección -->
    <div class="row-label" style="color: #B26A00;">Corrección</div>
    <v-row class="mb-4">
      <v-col cols="12" sm="4">
        <v-card
          outlined
          class="kpi-card pa-4 text-center"
          :class="{ 'kpi-card--clickable': trend.length > 1 }"
          @click="trend.length > 1 && openTrendDialog('correctionRate')"
        >
          <div class="kpi-label">Tasa de corrección</div>
          <div v-if="loadingKpi" class="py-2">
            <v-progress-circular indeterminate size="20" width="2" color="primary" />
          </div>
          <template v-else>
            <div class="kpi-value">{{ correctionRateLabel }}</div>
            <RefundSparkline :data="trendCorrectionRate" color="#F57C00" />
          </template>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card
          outlined
          class="kpi-card pa-4 text-center"
          :class="{ 'kpi-card--clickable': trend.length > 1 }"
          @click="trend.length > 1 && openTrendDialog('corrected')"
        >
          <div class="kpi-label">Reembolsos con corrección</div>
          <div v-if="loadingKpi" class="py-2">
            <v-progress-circular indeterminate size="20" width="2" color="primary" />
          </div>
          <template v-else>
            <div class="kpi-value">{{ insights.corrected != null ? insights.corrected : '—' }}</div>
            <RefundSparkline :data="trendCorrected" color="#F57C00" />
          </template>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined class="stat-card pa-3 text-center">
          <div class="stat-label">Servicio con más corrección</div>
          <div v-if="loadingInsights" class="py-2">
            <v-progress-circular indeterminate size="20" width="2" color="primary" />
          </div>
          <template v-else>
            <div class="stat-value">{{ topCorrectedServiceLabel }}</div>
            <div v-if="insights.topCorrectedService" class="stat-sub">
              {{ insights.topCorrectedService.count }} correcciones
            </div>
          </template>
        </v-card>
      </v-col>
    </v-row>

    <RefundTrendDialog
      v-model="trendDialogOpen"
      :title="trendDialogTitle"
      :points="trendDialogPoints"
      :color="trendDialogColor"
      :granularity="trendGranularity"
      :is-percentage="trendDialogMetric === 'rejectionRate' || trendDialogMetric === 'correctionRate'"
    />

    <!-- Por contrato: requiere `refund-metrics:view-contracts`. -->
    <RefundsByContractTable v-if="canSeeContracts" :from-date="fromDate" :to-date="toDate" />
  </v-container>
</template>

<script>
import moment from 'moment';
import RefundsByContractTable from '@/components/RefundsByContractTable.vue';
import RefundSparkline from '@/components/RefundSparkline.vue';
import RefundTrendDialog from '@/components/RefundTrendDialog.vue';
import RefundMetricsService from '@/services/refund-metrics.service';
import { can } from '@/utils/permissions';

const KPI_METRICS = {
  rejectionRate: { title: 'Tasa de rechazo', color: '#D32F2F', field: 'rejectionRate' },
  rejected: { title: 'Reembolsos rechazados', color: '#D32F2F', field: 'rejected' },
  correctionRate: { title: 'Tasa de corrección', color: '#F57C00', field: 'correctionRate' },
  corrected: { title: 'Reembolsos con corrección', color: '#F57C00', field: 'corrected' },
};

export default {
  name: 'MetricasReembolsos',
  components: { RefundsByContractTable, RefundSparkline, RefundTrendDialog },

  data() {
    return {
      canSeeContracts: can('refund-metrics:view-contracts'),
      period: 'historical',
      periodOffset: 0,
      loadingInsights: false,
      loadingTrend: false,
      insights: {
        total: null,
        rejected: null,
        rejectionRate: null,
        corrected: null,
        correctionRate: null,
        topRejectedService: null,
        topCorrectedService: null,
      },
      trend: [],
      trendDialogOpen: false,
      trendDialogMetric: 'rejectionRate',
    };
  },

  computed: {
    loadingKpi() {
      return this.loadingInsights || this.loadingTrend;
    },
    trendRejected() {
      return this.trend.map((t) => t.rejected);
    },
    trendRejectionRate() {
      return this.trend.map((t) => t.rejectionRate ?? 0);
    },
    trendCorrected() {
      return this.trend.map((t) => t.corrected);
    },
    trendCorrectionRate() {
      return this.trend.map((t) => t.correctionRate ?? 0);
    },
    trendGranularity() {
      if (this.period === 'month') return 'day';
      if (this.period === 'quarter') return 'week';
      return 'month';
    },
    periodUnit() {
      return this.period === 'month' ? 'month' : 'quarter';
    },
    periodBase() {
      return moment().add(this.periodOffset, `${this.periodUnit}s`);
    },
    // The current month/quarter has no data past today — cap toDate at today
    // instead of the period's calendar end so the trend chart doesn't show
    // an empty tail of future days.
    fromDate() {
      if (this.period === 'historical') return null;
      return this.periodBase.clone().startOf(this.periodUnit).format('YYYY-MM-DD');
    },
    toDate() {
      if (this.period === 'historical') return null;
      const end = this.periodBase.clone().endOf(this.periodUnit);
      const capped = this.periodOffset === 0 ? moment.min(end, moment()) : end;
      return capped.format('YYYY-MM-DD');
    },
    periodLabel() {
      if (this.period === 'month') {
        const label = this.periodBase.format('MMMM YYYY');
        return label.charAt(0).toUpperCase() + label.slice(1);
      }
      return `Q${this.periodBase.quarter()} ${this.periodBase.format('YYYY')}`;
    },
    canGoNext() {
      return this.periodOffset < 0;
    },
    rejectionRateLabel() {
      return this.insights.rejectionRate != null ? `${this.insights.rejectionRate.toFixed(1)}%` : '—';
    },
    correctionRateLabel() {
      return this.insights.correctionRate != null ? `${this.insights.correctionRate.toFixed(1)}%` : '—';
    },
    topRejectedServiceLabel() {
      return this.insights.topRejectedService ? this.insights.topRejectedService.title : 'Sin datos';
    },
    topCorrectedServiceLabel() {
      return this.insights.topCorrectedService ? this.insights.topCorrectedService.title : 'Sin datos';
    },
    trendDialogTitle() {
      return KPI_METRICS[this.trendDialogMetric].title;
    },
    trendDialogColor() {
      return KPI_METRICS[this.trendDialogMetric].color;
    },
    trendDialogPoints() {
      const field = KPI_METRICS[this.trendDialogMetric].field;
      return this.trend.map((t) => ({ period: t.period, value: t[field] ?? 0 }));
    },
  },

  watch: {
    period() {
      this.periodOffset = 0;
    },
    fromDate() {
      this.fetchInsights();
      this.fetchTrend();
    },
  },

  mounted() {
    this.fetchInsights();
    this.fetchTrend();
  },

  methods: {
    async fetchInsights() {
      this.loadingInsights = true;
      const res = await RefundMetricsService.getInsights(this.fromDate, this.toDate);
      if (res.status === 'ok') {
        this.insights = res.data;
      } else {
        this.insights = {
          total: null,
          rejected: null,
          rejectionRate: null,
          corrected: null,
          correctionRate: null,
          topRejectedService: null,
          topCorrectedService: null,
        };
      }
      this.loadingInsights = false;
    },

    async fetchTrend() {
      this.loadingTrend = true;
      const res = await RefundMetricsService.getTrend({
        granularity: this.trendGranularity,
        fromDate: this.fromDate,
        toDate: this.toDate,
      });
      this.trend = res.status === 'ok' ? res.data : [];
      this.loadingTrend = false;
    },

    openTrendDialog(metric) {
      this.trendDialogMetric = metric;
      this.trendDialogOpen = true;
    },
  },
};
</script>

<style scoped>
.header-row {
  gap: 16px;
}
.page-title {
  color: #104b94;
  font-weight: 700;
}
.page-desc {
  font-size: 13px;
  color: #555;
}
.total-badge {
  border-radius: 8px;
  border-color: #104b94;
  padding: 16px 26px;
  min-width: 220px;
}
.total-badge-label {
  text-align: right;
}
.total-badge-value {
  font-size: 34px;
  font-weight: 700;
  color: #104b94;
  font-family: Inter, sans-serif;
  line-height: 1.3;
  margin-top: 4px;
}
.toggle-label {
  font-size: 13px;
  color: #555;
  font-family: Inter, sans-serif;
  white-space: nowrap;
}
.period-toggle {
  border-radius: 8px !important;
  overflow: hidden;
}
.period-nav {
  gap: 2px;
}
.period-nav-label {
  font-size: 13px;
  font-weight: 600;
  color: #1B1D21;
  font-family: Inter, sans-serif;
  min-width: 130px;
  text-align: center;
}
.row-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-family: Inter, sans-serif;
  margin-bottom: 8px;
}
.kpi-card { border-radius: 8px; height: 100%; }
.kpi-card--clickable {
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}
.kpi-card--clickable:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}
.kpi-label {
  font-size: 11px;
  color: #8593A3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: Inter, sans-serif;
}
.kpi-value {
  font-size: 32px;
  font-weight: 700;
  color: #1B1D21;
  font-family: Inter, sans-serif;
  line-height: 1.3;
  margin-top: 4px;
}
.stat-card {
  border-radius: 8px;
  min-height: 100px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.stat-label {
  font-size: 11px;
  color: #8593A3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: Inter, sans-serif;
}
.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #1B1D21;
  font-family: Inter, sans-serif;
  line-height: 1.3;
  margin-top: 4px;
}
.stat-sub {
  font-size: 11px;
  color: #8593A3;
  font-family: Inter, sans-serif;
  margin-top: 2px;
}
</style>
